<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { percentOf } from './attributionUtils';

// "Rendimiento" tab of the CrmB mockup: filter bar, four KPIs, the two
// breakdown cards (por código / por fuente) and the funnel table.
const props = defineProps({
  reports: { type: Object, required: true },
  campaigns: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  rangeLabel: { type: String, default: '' },
});

const emit = defineEmits(['changeRange', 'export']);

const { t, n } = useI18n();

const totalLeads = computed(() => Number(props.reports.total_leads) || 0);
const funnel = computed(() => props.reports.funnel || {});

const descriptionFor = code =>
  props.campaigns.find(campaign => campaign.code === code)?.utms?.campaign ||
  props.campaigns.find(campaign => campaign.code === code)?.description ||
  '—';

// Sorted [label, count] pairs with the percentage of the period's leads.
const rankOf = (map, limit = 6) =>
  Object.entries(map || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([label, count]) => ({
      label,
      count,
      pct: percentOf(count, totalLeads.value),
      pctLabel: `${percentOf(count, totalLeads.value)}%`,
    }));

const codeRank = computed(() => rankOf(props.reports.campaigns));
const sourceRank = computed(() => rankOf(props.reports.sources, 4));

const maxCode = computed(() =>
  Math.max(1, ...codeRank.value.map(row => row.count))
);
const maxSource = computed(() =>
  Math.max(1, ...sourceRank.value.map(row => row.count))
);

const topSource = computed(() => sourceRank.value[0]);
const topCode = computed(() => codeRank.value[0]);

const wonTotal = computed(() =>
  Object.values(funnel.value).reduce((sum, row) => sum + (row.won || 0), 0)
);

const kpis = computed(() => [
  {
    key: 'leads',
    label: t('ATTRIBUTION.KPI.LEADS'),
    value: n(totalLeads.value),
    note: t('ATTRIBUTION.KPI.LEADS_NOTE'),
  },
  {
    key: 'source',
    label: t('ATTRIBUTION.KPI.TOP_SOURCE'),
    value: topSource.value?.label || '—',
    note: topSource.value
      ? t('ATTRIBUTION.KPI.LEADS_WITH_PCT', {
          count: topSource.value.count,
          pct: topSource.value.pct,
        })
      : '',
  },
  {
    key: 'campaign',
    label: t('ATTRIBUTION.KPI.TOP_CAMPAIGN'),
    value: topCode.value?.label || '—',
    note: topCode.value
      ? t('ATTRIBUTION.KPI.LEADS_WITH_PCT', {
          count: topCode.value.count,
          pct: topCode.value.pct,
        })
      : '',
  },
  {
    key: 'conversion',
    label: t('ATTRIBUTION.KPI.CONVERSION'),
    value: `${percentOf(wonTotal.value, totalLeads.value)}%`,
    note: t('ATTRIBUTION.KPI.CONVERSION_NOTE', {
      won: wonTotal.value,
      total: totalLeads.value,
    }),
  },
]);

const funnelRows = computed(() =>
  Object.entries(funnel.value)
    .map(([code, row]) => ({
      code,
      campaign: descriptionFor(code),
      leads: row.leads || 0,
      quoted: row.quoted || 0,
      won: row.won || 0,
      conversion: percentOf(row.won || 0, row.leads || 0),
      conversionLabel: `${percentOf(row.won || 0, row.leads || 0)}%`,
    }))
    .sort((a, b) => b.leads - a.leads)
);

const TH_CLASS =
  'py-2.5 px-3 text-[10.5px] font-semibold tracking-[0.07em] uppercase text-n-slate-10';
const CARD_CLASS =
  'flex flex-col gap-3 px-4 py-[15px] rounded-xl border border-n-weak bg-n-solid-2';
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Filtros -->
    <div
      class="flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-xl border border-n-weak bg-n-solid-2"
    >
      <button
        type="button"
        class="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg bg-n-alpha-2 text-xs hover:bg-n-alpha-3 transition-colors"
        @click="emit('changeRange')"
      >
        <span class="text-n-slate-10">{{
          t('ATTRIBUTION.FILTERS.DATES')
        }}</span>
        <span class="font-semibold text-n-slate-12">{{ rangeLabel }}</span>
        <Icon icon="i-lucide-chevron-down" size="size-3 opacity-55" />
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-1.5 h-[30px] px-2.5 ml-auto rounded-lg border border-n-weak text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
        @click="emit('export')"
      >
        <Icon icon="i-lucide-download" size="size-3" />
        {{ t('ATTRIBUTION.FILTERS.EXPORT') }}
      </button>
    </div>

    <div
      v-if="isLoading"
      class="flex items-center justify-center py-16 text-n-slate-11"
    >
      <Spinner />
    </div>

    <template v-else>
      <!-- KPIs -->
      <div class="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <div
          v-for="kpi in kpis"
          :key="kpi.key"
          :class="CARD_CLASS"
          class="!gap-1.5"
        >
          <span
            class="text-[11px] font-semibold tracking-[0.06em] uppercase text-n-slate-10"
          >
            {{ kpi.label }}
          </span>
          <span
            class="text-[22px] font-semibold tracking-tight truncate text-n-slate-12"
            :title="String(kpi.value)"
          >
            {{ kpi.value }}
          </span>
          <span class="text-[11.5px] truncate text-n-slate-10">{{
            kpi.note
          }}</span>
        </div>
      </div>

      <!-- Desgloses -->
      <div class="grid grid-cols-1 gap-3 lg:grid-cols-2 items-start">
        <section :class="CARD_CLASS">
          <h3 class="m-0 text-sm font-semibold text-n-slate-12">
            {{ t('ATTRIBUTION.BREAKDOWN.BY_CODE') }}
          </h3>
          <p v-if="!codeRank.length" class="m-0 text-xs text-n-slate-10">
            {{ t('ATTRIBUTION.EMPTY_RANGE') }}
          </p>
          <div
            v-for="row in codeRank"
            :key="row.label"
            class="grid grid-cols-[64px_1fr_34px_40px] gap-2.5 items-center"
          >
            <span
              class="inline-flex items-center h-[21px] px-2 rounded-md bg-n-solid-blue text-n-blue-text text-[11.5px] font-semibold font-mono justify-self-start"
            >
              {{ row.label }}
            </span>
            <div class="h-2 rounded-full bg-n-alpha-2 overflow-hidden">
              <div
                class="h-full rounded-full bg-n-brand"
                :style="{
                  width: `${Math.round((row.count / maxCode) * 100)}%`,
                }"
              />
            </div>
            <span
              class="text-[12.5px] font-semibold text-right tabular-nums text-n-slate-12"
            >
              {{ row.count }}
            </span>
            <span class="text-xs text-right text-n-slate-10">{{
              row.pctLabel
            }}</span>
          </div>
        </section>

        <section :class="CARD_CLASS">
          <h3 class="m-0 text-sm font-semibold text-n-slate-12">
            {{ t('ATTRIBUTION.BREAKDOWN.BY_SOURCE') }}
          </h3>
          <p v-if="!sourceRank.length" class="m-0 text-xs text-n-slate-10">
            {{ t('ATTRIBUTION.EMPTY_RANGE') }}
          </p>
          <div
            v-for="row in sourceRank"
            :key="row.label"
            class="grid grid-cols-[88px_1fr_34px_40px] gap-2.5 items-center"
          >
            <span class="text-[12.5px] font-medium truncate text-n-slate-12">
              {{ row.label }}
            </span>
            <div class="h-2 rounded-full bg-n-alpha-2 overflow-hidden">
              <div
                class="h-full rounded-full bg-n-brand"
                :style="{
                  width: `${Math.round((row.count / maxSource) * 100)}%`,
                }"
              />
            </div>
            <span
              class="text-[12.5px] font-semibold text-right tabular-nums text-n-slate-12"
            >
              {{ row.count }}
            </span>
            <span class="text-xs text-right text-n-slate-10">{{
              row.pctLabel
            }}</span>
          </div>
        </section>
      </div>

      <!-- Embudo por código -->
      <section
        class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
      >
        <table class="w-full table-fixed border-collapse">
          <thead>
            <tr class="border-b border-n-weak bg-n-alpha-1">
              <th
                :class="TH_CLASS"
                class="text-left ltr:pl-4 rtl:pr-4 w-[86px]"
              >
                {{ t('ATTRIBUTION.TABLE.CODE') }}
              </th>
              <th :class="TH_CLASS" class="text-left">
                {{ t('ATTRIBUTION.TABLE.CAMPAIGN') }}
              </th>
              <th :class="TH_CLASS" class="text-right w-[80px]">
                {{ t('ATTRIBUTION.TABLE.LEADS') }}
              </th>
              <th :class="TH_CLASS" class="text-right w-[100px]">
                {{ t('ATTRIBUTION.TABLE.QUOTED') }}
              </th>
              <th :class="TH_CLASS" class="text-right w-[92px]">
                {{ t('ATTRIBUTION.TABLE.WON') }}
              </th>
              <th
                :class="TH_CLASS"
                class="text-right ltr:pr-4 rtl:pl-4 w-[100px]"
              >
                {{ t('ATTRIBUTION.TABLE.CONVERSION') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!funnelRows.length">
              <td colspan="6" class="py-10 text-center text-xs text-n-slate-10">
                {{ t('ATTRIBUTION.EMPTY_RANGE') }}
              </td>
            </tr>
            <tr
              v-for="row in funnelRows"
              :key="row.code"
              class="border-b border-n-weak last:border-b-0"
            >
              <td class="py-3 px-3 ltr:pl-4 rtl:pr-4 align-middle">
                <span
                  class="inline-flex items-center h-[21px] px-2 rounded-md bg-n-alpha-2 text-[11.5px] font-semibold font-mono text-n-slate-11"
                >
                  {{ row.code }}
                </span>
              </td>
              <td
                class="py-3 px-3 align-middle text-[12.5px] truncate text-n-slate-11"
                :title="row.campaign"
              >
                {{ row.campaign }}
              </td>
              <td
                class="py-3 px-3 align-middle text-[12.5px] font-semibold text-right tabular-nums text-n-slate-12"
              >
                {{ row.leads }}
              </td>
              <td
                class="py-3 px-3 align-middle text-[12.5px] text-right tabular-nums text-n-slate-11"
              >
                {{ row.quoted }}
              </td>
              <td
                class="py-3 px-3 align-middle text-[12.5px] text-right tabular-nums text-n-slate-11"
              >
                {{ row.won }}
              </td>
              <td
                class="py-3 px-3 ltr:pr-4 rtl:pl-4 align-middle text-[12.5px] font-semibold text-right tabular-nums"
                :class="
                  row.conversion >= 25
                    ? 'text-n-teal-11'
                    : row.conversion > 0
                      ? 'text-n-slate-11'
                      : 'text-n-slate-9'
                "
              >
                {{ row.conversionLabel }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>
